import { useState, useEffect, useRef } from 'react';
import { Check } from 'lucide-react';

export interface DropdownOption {
  value: string;
  label: string;
}

export interface DropdownProps {
  options: DropdownOption[];
  selected: string[];
  onChange: (selected: string[]) => void;
  placeholder: string;
  multiple?: boolean;
  /** Visual size. `sm` matches the compact dashboard embedding, `md` matches full-page filter rows. */
  size?: 'sm' | 'md';
}

export function Dropdown({
  options,
  selected,
  onChange,
  placeholder,
  multiple = false,
  size = 'md',
}: DropdownProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleOption = (value: string) => {
    if (multiple) {
      onChange(selected.includes(value) ? selected.filter(s => s !== value) : [...selected, value]);
    } else {
      onChange(value ? [value] : []);
      setOpen(false);
    }
  };

  const displayText =
    selected.length === 0
      ? placeholder
      : multiple && selected.length > 1
        ? `${selected.length} выбрано`
        : options.find(o => o.value === selected[0])?.label ?? selected[0];

  const buttonPadding = size === 'sm' ? 'px-3 py-1.5 min-w-[110px]' : 'px-3 py-2 min-w-[130px]';
  const buttonGap = size === 'sm' ? 'gap-1.5' : 'gap-2';

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className={`flex items-center ${buttonGap} ${buttonPadding} bg-surface border border-border rounded-lg text-sm text-foreground cursor-pointer justify-between hover:border-foreground-muted transition-colors`}
      >
        {displayText}
        <span className="text-xs text-foreground-muted">▾</span>
      </button>
      {open && (
        <div className="absolute top-[calc(100%+4px)] left-0 z-[100] bg-surface border border-border rounded-lg shadow-lg min-w-full max-h-[250px] overflow-y-auto">
          <div
            className={`flex items-center gap-2 px-3 py-2 text-sm cursor-pointer transition-colors hover:bg-surface-hover ${selected.length === 0 ? 'text-ctp-blue bg-ctp-blue/10' : 'text-foreground'}`}
            onClick={() => { onChange([]); setOpen(false); }}
          >
            {placeholder}
          </div>
          {options.map(option => (
            <div
              key={option.value}
              className={`flex items-center gap-2 px-3 py-2 text-sm cursor-pointer transition-colors hover:bg-surface-hover ${selected.includes(option.value) ? 'text-ctp-blue bg-ctp-blue/10' : 'text-foreground'}`}
              onClick={() => toggleOption(option.value)}
            >
              {multiple && (
                <span className={`w-4 h-4 border rounded flex items-center justify-center flex-shrink-0 ${selected.includes(option.value) ? 'bg-ctp-blue border-ctp-blue text-white' : 'border-border'}`}>
                  {selected.includes(option.value) && <Check size={11} />}
                </span>
              )}
              {option.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default Dropdown;
